import {
  DataTable,
  FilterBar,
  PageHeader,
  Skeleton,
  Tabs,
  type Column,
  type FilterConfig,
} from '@zyxantech/tux'
import { AlarmList } from '@zyxantech/wiz'
import { useMemo, useState } from 'react'
import {
  useAcknowledgeAlarmMutation,
  useGetAlarmEventsQuery,
  type AlarmEvent,
} from '@/api/alarms.api'
import { useGetTagsQuery } from '@/api/tags.api'
import { AlarmSeverityBadge } from '@/components/alarms/AlarmSeverityBadge'
import { ErrorState } from '@/components/shared/ErrorState'
import { PAGINATION } from '@/constants/pagination'
import { ACTION, RESOURCE } from '@/constants/permissions'
import { TIME } from '@/constants/time'
import { usePermission } from '@/hooks/usePermission'
import { formatTimestamp, formatValue } from '@/utils/format'
import { compareAlarmItems, toAlarmItem } from './alarm.utils'

type TabId = 'active' | 'acknowledged' | 'history'

export function AlarmsPage() {
  const [tab, setTab] = useState<TabId>('active')
  const [severity, setSeverity] = useState('')
  const canAck = usePermission(RESOURCE.ALARM, ACTION.UPDATE)

  // Resolve tagId → name for human-readable alarm sources.
  const { data: tagsData } = useGetTagsQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })
  const tagName = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of tagsData?.items ?? []) map.set(t.id, t.name)
    return map
  }, [tagsData])

  const sourceFor = (e: AlarmEvent) => tagName.get(e.tagId) ?? e.tagId

  return (
    <div className="p-6">
      <PageHeader title="Alarms" subtitle="Monitor and acknowledge plant alarms" />

      <div className="pb-4">
        <Tabs
          items={[
            { id: 'active', label: 'Active' },
            { id: 'acknowledged', label: 'Acknowledged' },
            { id: 'history', label: 'History' },
          ]}
          value={tab}
          onChange={(t) => setTab(t as TabId)}
        />
      </div>

      {tab === 'active' && <ActiveTab canAck={canAck} sourceFor={sourceFor} />}
      {tab === 'acknowledged' && <TableTab status="acknowledged" sourceFor={sourceFor} />}
      {tab === 'history' && (
        <HistoryTab severity={severity} setSeverity={setSeverity} sourceFor={sourceFor} />
      )}
    </div>
  )
}

function ActiveTab({
  canAck,
  sourceFor,
}: {
  canAck: boolean
  sourceFor: (e: AlarmEvent) => string
}) {
  const { data, isLoading, error, refetch } = useGetAlarmEventsQuery(
    { status: 'active', page: PAGINATION.DEFAULT_PAGE, pageSize: PAGINATION.MAX_PAGE_SIZE },
    { pollingInterval: TIME.ALARM_REFRESH_MS },
  )
  const [acknowledge] = useAcknowledgeAlarmMutation()

  const items = useMemo(
    () => (data?.items ?? []).map((e) => toAlarmItem(e, sourceFor(e))).sort(compareAlarmItems),
    [data, sourceFor],
  )

  if (isLoading) return <Skeleton height={200} />
  if (error) return <ErrorState error={error} retry={() => void refetch()} />

  // Only pass onAcknowledge when permitted — exactOptionalPropertyTypes forbids
  // passing `undefined` to the optional prop.
  const ackProps = canAck
    ? {
        onAcknowledge: (id: string) => {
          void acknowledge(id)
        },
      }
    : {}

  return (
    <AlarmList
      alarms={items}
      showAcknowledgeButton={canAck}
      emptyMessage="No active alarms — all clear."
      {...ackProps}
    />
  )
}

/** Shared column set for the acknowledged / history tables. */
function useAlarmColumns(sourceFor: (e: AlarmEvent) => string): Column<AlarmEvent>[] {
  return [
    {
      key: 'severity',
      header: 'Severity',
      accessor: (a) => <AlarmSeverityBadge severity={a.severity} />,
    },
    { key: 'message', header: 'Message', accessor: (a) => a.message },
    { key: 'source', header: 'Source', accessor: (a) => sourceFor(a) },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      accessor: (a) => <span className="font-mono">{formatValue(a.triggeredValue)}</span>,
    },
    {
      key: 'triggeredAt',
      header: 'Triggered',
      accessor: (a) => <span className="font-mono text-xs">{formatTimestamp(a.triggeredAt)}</span>,
    },
  ]
}

function TableTab({
  status,
  sourceFor,
}: {
  status: 'acknowledged' | 'resolved'
  sourceFor: (e: AlarmEvent) => string
}) {
  const { data, isLoading, error, refetch } = useGetAlarmEventsQuery({
    status,
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })
  const columns = useAlarmColumns(sourceFor)

  if (error) return <ErrorState error={error} retry={() => void refetch()} />
  return (
    <DataTable
      columns={columns}
      data={data?.items ?? []}
      loading={isLoading}
      rowKey={(a) => a.id}
      emptyMessage="No acknowledged alarms."
    />
  )
}

function HistoryTab({
  severity,
  setSeverity,
  sourceFor,
}: {
  severity: string
  setSeverity: (v: string) => void
  sourceFor: (e: AlarmEvent) => string
}) {
  const { data, isLoading, error, refetch } = useGetAlarmEventsQuery({
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
    ...(severity ? { severity: severity as 'info' | 'warning' | 'critical' } : {}),
  })
  const columns = useAlarmColumns(sourceFor)

  const filters: FilterConfig[] = [
    {
      key: 'severity',
      type: 'select',
      label: 'Severity',
      placeholder: 'All severities',
      options: [
        { label: 'Critical', value: 'critical' },
        { label: 'Warning', value: 'warning' },
        { label: 'Info', value: 'info' },
      ],
    },
  ]

  return (
    <div className="space-y-4">
      <FilterBar
        filters={filters}
        values={{ severity }}
        onChange={(_key, value) => setSeverity(typeof value === 'string' ? value : '')}
        onClear={() => setSeverity('')}
      />
      {error ? (
        <ErrorState error={error} retry={() => void refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          loading={isLoading}
          rowKey={(a) => a.id}
          emptyMessage="No alarm history."
        />
      )}
    </div>
  )
}
